import { type StatsigLayerName, useLayer } from '@coursera-web/statsig';

import isMobileApp from 'js/lib/isMobileApp';
import { get as getUser } from 'js/lib/user';

import { useProductFeatures } from 'bundles/enroll-common/utils/productFeatureFlags';
import { IdTypes } from 'bundles/product-features';

const LAYER_NAME: StatsigLayerName = 'revoke-access-and-limited-free-trial-layer';
const PARAM_NAME = 'revoke-and-limited-trial-variant';

type Variant = 'control' | 'revoke-access' | 'limited-free-trial' | 'revoke-and-limited-trial';

/**
 * Reads the learner's variant of the Revoke Access and Limited Free Trial experiment. Reading the
 * layer param is itself what logs the Statsig exposure, so we skip the read (returning 'control'
 * instead) if the learner should be excluded (a pre-existing subscriber,
 * MONETIZE-3123/3153; or mobile app, MONETIZE-3163), the exclusion check
 * hasn't resolved yet, or the caller says this render isn't a real exposure
 * (e.g. no free trial is actually on offer here).
 * https://console.statsig.com/2aQACzHI81tdbY41wEZm3H/layers/revoke-access-and-limited-free-trial-layer
 */
export const useRevokeAccessAndLimitedTrialVariant = (
  shouldRecordExposure: boolean = true
): { variant: Variant; loading: boolean } => {
  const { features, loading, error } = useProductFeatures({
    idType: IdTypes.User,
    id: String(getUser().id),
  });
  const isExcludedFromExperiment = features.get(
    'commerceMonetization',
    'excludeLearnerSubscribedPriorToCurrentMonetizationExperiment'
  );
  const layer = useLayer(LAYER_NAME);
  if (loading) {
    return { variant: 'control', loading: true };
  }
  // Fail closed: an errored exclusion check is treated as excluded, not as eligible.
  // Mobile is excluded from FE exposure only (MONETIZE-3163); BE item locking still
  // evaluates Statsig once mobile targeting is removed from the experiment gate.
  if (error || isExcludedFromExperiment || isMobileApp.get() || !shouldRecordExposure) {
    return { variant: 'control', loading: false };
  }
  return { variant: layer.get(PARAM_NAME, 'control') as Variant, loading: false };
};

export const useIsRevokeAccessEnabled = (
  shouldRecordExposure: boolean = true
): { isRevokeAccessEnabled: boolean; loading: boolean } => {
  const { variant, loading } = useRevokeAccessAndLimitedTrialVariant(shouldRecordExposure);
  return { isRevokeAccessEnabled: variant === 'revoke-access' || variant === 'revoke-and-limited-trial', loading };
};

export const useIsLimitedFreeTrialEnabled = (
  shouldRecordExposure: boolean = true
): { isLimitedFreeTrialEnabled: boolean; loading: boolean } => {
  const { variant, loading } = useRevokeAccessAndLimitedTrialVariant(shouldRecordExposure);
  return {
    isLimitedFreeTrialEnabled: variant === 'limited-free-trial' || variant === 'revoke-and-limited-trial',
    loading,
  };
};
